import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ServiceSelection,
  BasicDetails,
  Address,
  BankDetails,
  PANGSTDetails,
} from '@/types/vendor';
import { logout } from './authSlice';

export interface UploadedDocument {
  documentId: string;
  documentType: 'aadhaar_front' | 'aadhaar_back' | 'pan_card' | 'gst_certificate' | 'cancelled_cheque';
  fileName: string;
  fileSize: number;
  fileUrl: string;
  uploadedAt: string;
}

export interface FormState {
  serviceSelection: ServiceSelection | null;
  basicDetails: BasicDetails | null;
  address: Address | null;
  bankDetails: BankDetails | null;
  panGstDetails: PANGSTDetails | null;
  documents: UploadedDocument[];
  isDirty: boolean;
  lastSaved: string | null;
}

const initialState: FormState = {
  serviceSelection: null,
  basicDetails: null,
  address: null,
  bankDetails: null,
  panGstDetails: null,
  documents: [],
  isDirty: false,
  lastSaved: null,
};

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    setServiceSelection: (state, action: PayloadAction<ServiceSelection>) => {
      state.serviceSelection = action.payload;
      state.isDirty = true;
    },
    setBasicDetails: (state, action: PayloadAction<BasicDetails>) => {
      state.basicDetails = action.payload;
      state.isDirty = true;
    },
    updateBasicDetails: (state, action: PayloadAction<Partial<BasicDetails>>) => {
      if (state.basicDetails) {
        state.basicDetails = { ...state.basicDetails, ...action.payload };
      } else {
        state.basicDetails = action.payload as BasicDetails;
      }
      state.isDirty = true;
    },
    setAddress: (state, action: PayloadAction<Address>) => {
      state.address = action.payload;
      state.isDirty = true;
    },
    updateAddress: (state, action: PayloadAction<Partial<Address>>) => {
      if (state.address) {
        state.address = { ...state.address, ...action.payload };
      } else {
        state.address = action.payload as Address;
      }
      state.isDirty = true;
    },
    setBankDetails: (state, action: PayloadAction<BankDetails>) => {
      state.bankDetails = action.payload;
      state.isDirty = true;
    },
    updateBankDetails: (state, action: PayloadAction<Partial<BankDetails>>) => {
      if (state.bankDetails) {
        state.bankDetails = { ...state.bankDetails, ...action.payload };
      } else {
        state.bankDetails = action.payload as BankDetails;
      }
      state.isDirty = true;
    },
    setPANGSTDetails: (state, action: PayloadAction<PANGSTDetails>) => {
      state.panGstDetails = action.payload;
      state.isDirty = true;
    },
    updatePANGSTDetails: (state, action: PayloadAction<Partial<PANGSTDetails>>) => {
      if (state.panGstDetails) {
        state.panGstDetails = { ...state.panGstDetails, ...action.payload };
      } else {
        state.panGstDetails = action.payload as PANGSTDetails;
      }
      state.isDirty = true;
    },
    setDocuments: (state, action: PayloadAction<UploadedDocument[]>) => {
      state.documents = action.payload;
      state.isDirty = true;
    },
    addDocument: (state, action: PayloadAction<UploadedDocument>) => {
      state.documents.push(action.payload);
      state.isDirty = true;
    },
    removeDocument: (state, action: PayloadAction<string>) => {
      state.documents = state.documents.filter(
        (doc) => doc.documentId !== action.payload
      );
      state.isDirty = true;
    },
    markFormSaved: (state) => {
      state.isDirty = false;
      state.lastSaved = new Date().toISOString();
    },
    clearFormDraft: (state) => {
      state.serviceSelection = null;
      state.basicDetails = null;
      state.address = null;
      state.bankDetails = null;
      state.panGstDetails = null;
      state.documents = [];
      state.isDirty = false;
      state.lastSaved = null;
    },
    hydrateFromFirestore: (
      state,
      action: PayloadAction<{
        basicDetails?: BasicDetails;
        address?: Address;
        bankDetails?: Partial<BankDetails>;
        panNumber?: string;
        panName?: string;
        gstNumber?: string;
        gstBusinessName?: string;
      }>
    ) => {
      const { basicDetails, address, bankDetails, panNumber, panName, gstNumber, gstBusinessName } = action.payload;
      if (basicDetails) state.basicDetails = basicDetails;
      if (address) state.address = address;
      if (bankDetails && Object.keys(bankDetails).length > 0) state.bankDetails = bankDetails as BankDetails;
      if (panNumber || gstNumber) {
        state.panGstDetails = {
          panNumber: panNumber || '',
          panName,
          isPANVerified: false,
          gstRegistered: !!gstNumber,
          gstNumber,
          gstBusinessName,
          isGSTVerified: false,
          vendorType: 'individual',
          vendorBusinessName: '',
          hasSkillIndiaCertificate: false,
        };
      }
      state.isDirty = false;
      state.lastSaved = new Date().toISOString();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  setServiceSelection,
  setBasicDetails,
  updateBasicDetails,
  setAddress,
  updateAddress,
  setBankDetails,
  updateBankDetails,
  setPANGSTDetails,
  updatePANGSTDetails,
  setDocuments,
  addDocument,
  removeDocument,
  markFormSaved,
  clearFormDraft,
  hydrateFromFirestore,
} = formSlice.actions;

export default formSlice.reducer;